import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  TrendingUp, Plus, Filter, Search, Download, 
  DollarSign, Clock, CheckCircle, XCircle, 
  AlertTriangle, ArrowUpRight, ArrowDownRight, RefreshCw,
  Eye, Edit, MoreVertical, Calendar, MapPin,
  Truck, Globe, Shield, FileText, PieChart,
  Activity, ShoppingCart, CreditCard, Receipt, Thermometer,
  Droplets, Zap, Box, Palette, Layers, Archive,
  Target, TrendingDown, Users, Package, Settings, BarChart3,
  LineChart, CalendarDays, Clock4, Brain
} from 'lucide-react';
import { 
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, ScatterChart, Scatter
} from 'recharts';

const Planning = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPlan, setSelectedPlan] = useState('all');
  const [timeRange, setTimeRange] = useState('12m');
  const { isDarkMode } = useChainsTheme();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock data for planning and forecasting
  const planningMetrics = [
    { 
      name: 'Demand Forecast', 
      value: 'SAR 3.2M', 
      change: '+8.5%', 
      trend: 'up', 
      icon: TrendingUp, 
      color: 'blue',
      accuracy: 94.2,
      confidence: 'high'
    },
    { 
      name: 'Supply Capacity', 
      value: '125%', 
      change: '+5.2%', 
      trend: 'up', 
      icon: Target, 
      color: 'green',
      accuracy: 91.8,
      confidence: 'medium'
    },
    { 
      name: 'Inventory Planning', 
      value: 'SAR 1.8M', 
      change: '+12.1%', 
      trend: 'up', 
      icon: Package, 
      color: 'purple',
      accuracy: 96.5,
      confidence: 'high'
    },
    { 
      name: 'Cost Optimization', 
      value: 'SAR 650K', 
      change: '+15.8%', 
      trend: 'up', 
      icon: DollarSign, 
      color: 'emerald',
      accuracy: 89.3,
      confidence: 'medium'
    },
    { 
      name: 'Lead Time', 
      value: '8.5 days', 
      change: '-12.3%', 
      trend: 'down', 
      icon: Clock, 
      color: 'cyan',
      accuracy: 92.7,
      confidence: 'high'
    },
    { 
      name: 'Forecast Accuracy', 
      value: '94.2%', 
      change: '+2.1%', 
      trend: 'up', 
      icon: Brain, 
      color: 'orange',
      accuracy: 94.2,
      confidence: 'high'
    }
  ];

  const demandForecastData = [
    { month: 'Jan', actual: 1250000, forecast: 1280000, variance: 2.4, confidence: 92 },
    { month: 'Feb', actual: 1380000, forecast: 1350000, variance: -2.2, confidence: 94 },
    { month: 'Mar', actual: 1520000, forecast: 1480000, variance: -2.6, confidence: 91 },
    { month: 'Apr', actual: 1680000, forecast: 1650000, variance: -1.8, confidence: 95 },
    { month: 'May', actual: 1820000, forecast: 1780000, variance: -2.2, confidence: 93 },
    { month: 'Jun', actual: 1950000, forecast: 1920000, variance: -1.5, confidence: 96 },
    { month: 'Jul', actual: null, forecast: 2050000, variance: null, confidence: 94 },
    { month: 'Aug', actual: null, forecast: 2180000, variance: null, confidence: 92 },
    { month: 'Sep', actual: null, forecast: 2250000, variance: null, confidence: 90 },
    { month: 'Oct', actual: null, forecast: 2320000, variance: null, confidence: 88 },
    { month: 'Nov', actual: null, forecast: 2450000, variance: null, confidence: 85 },
    { month: 'Dec', actual: null, forecast: 2580000, variance: null, confidence: 82 }
  ];

  const supplyPlanData = [
    { 
      id: 'SP-001', 
      name: 'Q1 Electronics Supply Plan', 
      category: 'Electronics', 
      period: 'Q1 2024', 
      status: 'active',
      totalDemand: 1250000,
      plannedSupply: 1320000,
      coverage: 105.6,
      cost: 850000,
      suppliers: 8,
      leadTime: 12,
      riskLevel: 'low',
      lastUpdated: '2024-01-15'
    },
    { 
      id: 'SP-002', 
      name: 'Q2 Logistics Capacity Plan', 
      category: 'Logistics', 
      period: 'Q2 2024', 
      status: 'draft',
      totalDemand: 1850000,
      plannedSupply: 1780000,
      coverage: 96.2,
      cost: 1200000,
      suppliers: 12,
      leadTime: 8,
      riskLevel: 'medium',
      lastUpdated: '2024-01-14'
    },
    { 
      id: 'SP-003', 
      name: 'Q3 Office Supplies Plan', 
      category: 'Office Supplies', 
      period: 'Q3 2024', 
      status: 'review',
      totalDemand: 950000,
      plannedSupply: 1020000,
      coverage: 107.4,
      cost: 680000,
      suppliers: 6,
      leadTime: 15,
      riskLevel: 'low',
      lastUpdated: '2024-01-13'
    },
    { 
      id: 'SP-004', 
      name: 'Q4 Transportation Plan', 
      category: 'Transportation', 
      period: 'Q4 2024', 
      status: 'draft',
      totalDemand: 2100000,
      plannedSupply: 1950000,
      coverage: 92.9,
      cost: 1450000,
      suppliers: 15,
      leadTime: 6,
      riskLevel: 'high',
      lastUpdated: '2024-01-12'
    }
  ];

  const capacityPlanningData = [
    { 
      id: 'CP-001', 
      name: 'Riyadh Warehouse', 
      type: 'Storage', 
      currentCapacity: 85000, 
      plannedCapacity: 95000,
      utilization: 78,
      efficiency: 94.2,
      expansionCost: 250000,
      timeline: '6 months',
      status: 'approved'
    },
    { 
      id: 'CP-002', 
      name: 'Jeddah Distribution Center', 
      type: 'Distribution', 
      currentCapacity: 92000, 
      plannedCapacity: 105000,
      utilization: 85,
      efficiency: 96.8,
      expansionCost: 380000,
      timeline: '8 months',
      status: 'in_review'
    },
    { 
      id: 'CP-003', 
      name: 'Dammam Processing Plant', 
      type: 'Processing', 
      currentCapacity: 78000, 
      plannedCapacity: 88000,
      utilization: 72,
      efficiency: 91.5,
      expansionCost: 420000,
      timeline: '12 months',
      status: 'draft'
    }
  ];

  const categoryData = [
    { name: 'Electronics', value: 35, color: '#3B82F6' },
    { name: 'Logistics', value: 25, color: '#10B981' },
    { name: 'Office Supplies', value: 20, color: '#F59E0B' },
    { name: 'Transportation', value: 15, color: '#EF4444' },
    { name: 'Raw Materials', value: 5, color: '#8B5CF6' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'draft': return 'text-blue-400';
      case 'review': return 'text-orange-400';
      case 'approved': return 'text-green-400';
      case 'in_review': return 'text-yellow-400';
      case 'rejected': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20';
      case 'draft': return 'bg-blue-500/20';
      case 'review': return 'bg-orange-500/20';
      case 'approved': return 'bg-green-500/20';
      case 'in_review': return 'bg-yellow-500/20';
      case 'rejected': return 'bg-red-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'high': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getRiskBg = (risk) => {
    switch (risk) {
      case 'low': return 'bg-green-500/20';
      case 'medium': return 'bg-yellow-500/20';
      case 'high': return 'bg-red-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const PlanningMetricCard = ({ metric }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${metric.color}-500/20`}>
          <metric.icon className={`w-6 h-6 text-${metric.color}-500`} />
        </div>
        <div className={`flex items-center text-sm ${
          metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
        }`}>
          {metric.trend === 'up' ? (
            <ArrowUpRight className="w-4 h-4 mr-1" />
          ) : (
            <ArrowDownRight className="w-4 h-4 mr-1" />
          )}
          {metric.change}
        </div>
      </div>
      
      <div className="mb-4">
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.value}</p>
        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{metric.name}</p>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Accuracy</span>
          <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.accuracy}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${metric.accuracy}%` }}
            transition={{ duration: 1, delay: 0.2 }}
            className={`h-2 rounded-full ${
              metric.accuracy >= 90 ? 'bg-green-500' :
              metric.accuracy >= 75 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
          />
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Confidence</span>
          <span className={`text-xs font-medium ${
            metric.confidence === 'high' ? 'text-green-400' :
            metric.confidence === 'medium' ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {metric.confidence}
          </span>
        </div>
      </div>
    </motion.div>
  );

  const SupplyPlanCard = ({ plan }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(plan.status)} ${getStatusColor(plan.status)}`}>
              {plan.status}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{plan.category} • {plan.period}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskBg(plan.riskLevel)} ${getRiskColor(plan.riskLevel)}`}>
          {plan.riskLevel} risk
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Demand</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {plan.totalDemand.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Supply</p>
          <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {plan.plannedSupply.toLocaleString()}</p>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Coverage</span>
          <span className={`font-medium ${plan.coverage >= 100 ? 'text-green-400' : 'text-orange-400'}`}>
            {plan.coverage}%
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Lead Time</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{plan.leadTime} days</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Suppliers</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{plan.suppliers}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Cost</span>
          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {plan.cost.toLocaleString()}</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="text-sm">
          <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Updated: {plan.lastUpdated}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
                      <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Planning & Forecasting</h1>
          <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-700'}`}>Demand forecasting, supply planning, and capacity optimization</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <Download className="w-5 h-5 mr-2 inline" />
            Export Plans
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            Create Plan
          </motion.button>
        </div>
      </motion.div>

      {/* Planning Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {planningMetrics.map((metric, index) => (
          <motion.div
            key={metric.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <PlanningMetricCard metric={metric} />
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Demand Forecast */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Demand Forecast</h3>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Actual</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Forecast</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={demandForecastData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#f0f0f0'} />
              <XAxis dataKey="month" stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <YAxis stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
              <Line type="monotone" dataKey="actual" stroke="#3B82F6" strokeWidth={3} />
              <Line type="monotone" dataKey="forecast" stroke="#10B981" strokeWidth={3} strokeDasharray="5 5" />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Category Distribution */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Planning by Category</h3>
            <button className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}>View All</button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {categoryData.map((category, index) => (
              <div key={category.name} className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }}></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-700'}`}>{category.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-1 p-1 backdrop-blur-sm rounded-xl border border-white/20">
        {[
          { id: 'overview', label: 'Overview', count: planningMetrics.length },
          { id: 'supply-plans', label: 'Supply Plans', count: supplyPlanData.length },
          { id: 'capacity', label: 'Capacity Planning', count: capacityPlanningData.length },
          { id: 'forecasting', label: 'Forecasting', count: 8 }
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <span>{tab.label}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
              activeTab === tab.id ? 'bg-white/20' : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
            }`}>
              {tab.count}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Content based on active tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'supply-plans' && (
          <motion.div
            key="supply-plans"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {supplyPlanData.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <SupplyPlanCard plan={plan} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Active Supply Plans</h3>
              {supplyPlanData.filter(plan => plan.status === 'active').slice(0, 2).map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <SupplyPlanCard plan={plan} />
                </motion.div>
              ))}
            </div>
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Top Planning Metrics</h3>
              <div className="space-y-4">
                {planningMetrics.slice(0, 3).map((metric, index) => (
                  <motion.div
                    key={metric.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`backdrop-blur-sm rounded-xl p-4 ${
                      isDarkMode 
                        ? 'bg-white/10 border border-white/20' 
                        : 'bg-white/70 border border-gray-200/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg bg-${metric.color}-500/20`}>
                          <metric.icon className={`w-5 h-5 text-${metric.color}-500`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{metric.name}</p>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{metric.value}</p>
                        </div>
                      </div>
                      <div className={`text-sm ${
                        metric.trend === 'up' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {metric.change}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'capacity' && (
          <motion.div
            key="capacity"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {capacityPlanningData.map((capacity, index) => (
              <motion.div
                key={capacity.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{capacity.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(capacity.status)} ${getStatusColor(capacity.status)}`}>
                        {capacity.status}
                      </span>
                    </div>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{capacity.type}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Current</p>
                    <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{capacity.currentCapacity.toLocaleString()} sq ft</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Planned</p>
                    <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{capacity.plannedCapacity.toLocaleString()} sq ft</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Utilization</span>
                    <span className={`font-medium ${capacity.utilization >= 90 ? 'text-red-400' : 'text-green-400'}`}>
                      {capacity.utilization}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Efficiency</span>
                    <span className={`font-medium ${capacity.efficiency >= 90 ? 'text-green-400' : 'text-orange-400'}`}>
                      {capacity.efficiency}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Expansion Cost</span>
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SAR {capacity.expansionCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Timeline</span>
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{capacity.timeline}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/10">
                  <div className="text-sm">
                    <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Expansion: +{((capacity.plannedCapacity - capacity.currentCapacity) / capacity.currentCapacity * 100).toFixed(1)}%</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                    }`}>
                      <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
                    </button>
                    <button className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                    }`}>
                      <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Planning; 